/**
 * GlobalView — OpenSky Direct Browser Client
 *
 * Polls the OpenSky Network API directly from the user's browser.
 * This bypasses the Cloud Run egress restriction (OpenSky blocks Google Cloud IPs).
 * Each user's browser uses their own IP → not blocked.
 *
 * Flow:
 *   1. Fetch credentials from /api/v1/admin/opensky-credentials (admin endpoint)
 *   2. Try OAuth2 Bearer token from auth.opensky-network.org
 *      → Falls back permanently to anonymous if CORS blocks it (expected in browser)
 *   3. Poll opensky-network.org/api/states/all WITH viewport bounding box
 *      → Never fetches globally (would return 10k+ aircraft)
 *   4. Map raw data → Entity[] → push into entity store
 *   5. Track credits from X-Rate-Limit-Remaining header
 */

import type { Entity } from '@globalview/shared';
import { apiClient } from './api';

// ─── Types ───────────────────────────────────────────────────────

interface CredentialConfig {
  clientId: string;
  clientSecret: string;
}

interface OAuthToken {
  accessToken: string;
  expiresAt: number; // unix timestamp ms
  creditsRemaining: number | null;
}

export interface OpenSkyClientInfo {
  clientId: string;
  label: string;
  creditsRemaining: number | null;
  active: boolean;
}

export interface OpenSkyDirectStatus {
  mode: 'oauth2' | 'anonymous' | 'error' | 'idle';
  clientCount: number;
  activeClientIndex: number;
  clients: OpenSkyClientInfo[];
  entityCount: number;
  lastPollTime: number | null;
  pollIntervalMs: number;
  consecutiveErrors: number;
}

export type AircraftUpdateCallback = (entities: Entity[]) => void;
export type StatusUpdateCallback = (status: OpenSkyDirectStatus) => void;

// ─── Constants ───────────────────────────────────────────────────

const OPENSKY_API = 'https://opensky-network.org/api/states/all';
const OPENSKY_TOKEN_URL =
  'https://auth.opensky-network.org/auth/realms/opensky-network/protocol/openid-connect/token';

const TOKEN_REFRESH_MARGIN_S = 60;
const CREDIT_ROTATION_THRESHOLD = 50;
const DEFAULT_POLL_INTERVAL_MS = 15_000;
const MIN_POLL_INTERVAL_MS = 5_000;
const MAX_POLL_INTERVAL_MS = 60_000;

// ─── Viewport ────────────────────────────────────────────────────

export interface Viewport {
  lamin: number;
  lomin: number;
  lamax: number;
  lomax: number;
}

// ─── State ───────────────────────────────────────────────────────

let credentials: CredentialConfig[] = [];
let tokens: (OAuthToken | null)[] = [];
let activeClientIndex = 0;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let currentViewport: Viewport | null = null;
let pollIntervalMs = DEFAULT_POLL_INTERVAL_MS;
let consecutiveErrors = 0;
let lastPollTime: number | null = null;
let currentEntities: Entity[] = [];
let isRunning = false;

/**
 * Once OAuth2 fails with a CORS/network error, we stop trying forever.
 * auth.opensky-network.org does not allow browser cross-origin requests.
 */
let corsBlocked = false;

let onAircraftUpdate: AircraftUpdateCallback | null = null;
let onStatusUpdate: StatusUpdateCallback | null = null;

// ─── Status Helper ────────────────────────────────────────────────

function buildStatus(): OpenSkyDirectStatus {
  return {
    mode: corsBlocked || credentials.length === 0 ? 'anonymous' : 'oauth2',
    clientCount: credentials.length,
    activeClientIndex,
    clients: credentials.map((c, i) => ({
      clientId: c.clientId,
      label: c.clientId.split('@')[0] ?? c.clientId,
      creditsRemaining: tokens[i]?.creditsRemaining ?? null,
      active: i === activeClientIndex,
    })),
    entityCount: currentEntities.length,
    lastPollTime,
    pollIntervalMs,
    consecutiveErrors,
  };
}

function emitStatus(): void {
  onStatusUpdate?.(buildStatus());
}

// ─── Token Management ─────────────────────────────────────────────

async function getToken(index: number): Promise<string | null> {
  const cred = credentials[index];
  if (!cred) return null;
  if (corsBlocked) return null; // Already confirmed CORS blocks OAuth2

  const existing = tokens[index];
  const nowSec = Date.now() / 1000;
  if (existing && existing.expiresAt / 1000 - nowSec > TOKEN_REFRESH_MARGIN_S) {
    return existing.accessToken;
  }

  try {
    const params = new URLSearchParams({
      grant_type: 'client_credentials',
      client_id: cred.clientId,
      client_secret: cred.clientSecret,
    });

    const resp = await fetch(OPENSKY_TOKEN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
      signal: AbortSignal.timeout(10_000),
    });

    if (!resp.ok) {
      console.warn(`[OpenSkyDirect] Token fetch failed for client[${index}]: ${resp.status}`);
      return null;
    }

    const data = await resp.json() as { access_token: string; expires_in: number };
    tokens[index] = {
      accessToken: data.access_token,
      expiresAt: Date.now() + data.expires_in * 1000,
      creditsRemaining: existing?.creditsRemaining ?? null,
    };
    console.log(`[OpenSkyDirect] Token obtained for client[${index}]`);
    return tokens[index]!.accessToken;
  } catch (err) {
    if (err instanceof TypeError) {
      // TypeError = network failure or CORS — OAuth2 blocked from this origin
      corsBlocked = true;
      console.warn('[OpenSkyDirect] OAuth2 CORS blocked — switching permanently to anonymous');
      emitStatus();
    } else {
      console.warn(`[OpenSkyDirect] Token error for client[${index}]:`, err);
    }
    return null;
  }
}

function rotateClient(): void {
  if (credentials.length <= 1) return;
  activeClientIndex = (activeClientIndex + 1) % credentials.length;
  console.log(`[OpenSkyDirect] Rotated to client[${activeClientIndex}]`);
}

// ─── Raw Data Mapping ─────────────────────────────────────────────

// OpenSky REST state vector indices:
// [0]  icao24, [1] callsign, [2] origin_country, [3] time_position, [4] last_contact,
// [5]  longitude, [6] latitude, [7] baro_altitude, [8] on_ground, [9] velocity,
// [10] true_track, [11] vertical_rate, [12] sensors, [13] geo_altitude, [14] squawk,
// [15] spi, [16] position_source, [17] category
type StateVector = [
  string, string | null, string, number | null, number,
  number | null, number | null, number | null, boolean, number | null,
  number | null, number | null, number[] | null, number | null, string | null,
  boolean, number, number | undefined
];

function mapStateVectors(states: StateVector[]): Entity[] {
  return states
    .filter((s) => s[5] != null && s[6] != null)
    .map((s) => {
      const [icao24, callsign, , , lastContact, lon, lat, baroAlt, onGround,
             velocity, trueTrack, verticalRate, , geoAlt, , , , category] = s;

      const alt = geoAlt ?? baroAlt ?? 0;
      const props: Record<string, unknown> = {
        callsign: callsign?.trim() || undefined,
        onGround,
        velocity: velocity ?? 0,
        verticalRate: verticalRate ?? 0,
        category: category ?? 0,
        baroAltitude: baroAlt ?? undefined,
      };

      return {
        id: `air-${icao24}`,
        type: 'aircraft' as const,
        source: 'opensky-browser',
        position: { lat: lat!, lon: lon!, alt: Math.max(0, alt) },
        heading: trueTrack ?? 0,
        timestamp: new Date(lastContact * 1000).toISOString(),
        style: {},
        confidence: 1,
        properties: props,
      } as Entity;
    });
}

// ─── Poll ────────────────────────────────────────────────────────

async function poll(): Promise<void> {
  if (!isRunning) return;

  // CRITICAL: Never fetch globally — would return 10k+ aircraft and kill Cesium performance.
  // Viewport must be set by AirLayerRenderer from the Cesium camera.
  if (!currentViewport) {
    schedulePoll();
    return;
  }

  try {
    const { lamin, lomin, lamax, lomax } = currentViewport;
    const url = `${OPENSKY_API}?lamin=${lamin}&lomin=${lomin}&lamax=${lamax}&lomax=${lomax}`;

    const headers: Record<string, string> = { Accept: 'application/json' };

    // Attempt OAuth2 auth (skips immediately if corsBlocked)
    if (credentials.length > 0 && !corsBlocked) {
      const token = await getToken(activeClientIndex);
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else if (!corsBlocked) {
        // Try next client once
        rotateClient();
        const altToken = await getToken(activeClientIndex);
        if (altToken) {
          headers['Authorization'] = `Bearer ${altToken}`;
        }
      }
    }

    const resp = await fetch(url, {
      headers,
      signal: AbortSignal.timeout(15_000),
    });

    // Track credits from response headers
    const remaining = resp.headers.get('X-Rate-Limit-Remaining');
    if (remaining != null && tokens[activeClientIndex]) {
      tokens[activeClientIndex]!.creditsRemaining = parseInt(remaining, 10);
      if (parseInt(remaining, 10) < CREDIT_ROTATION_THRESHOLD && credentials.length > 1) {
        console.log(`[OpenSkyDirect] Credits low (${remaining}), rotating client`);
        rotateClient();
      }
    }

    if (resp.status === 429) {
      console.warn('[OpenSkyDirect] Rate limited, rotating client');
      rotateClient();
      pollIntervalMs = Math.min(pollIntervalMs * 2, MAX_POLL_INTERVAL_MS);
      consecutiveErrors++;
      emitStatus();
      schedulePoll();
      return;
    }

    if (!resp.ok) {
      throw new Error(`HTTP ${resp.status}`);
    }

    const data = await resp.json() as { states: StateVector[] | null };
    const entities = data.states ? mapStateVectors(data.states) : [];

    currentEntities = entities;
    lastPollTime = Date.now();
    consecutiveErrors = 0;

    onAircraftUpdate?.(entities);
    emitStatus();

    console.log(`[OpenSkyDirect] Polled → ${entities.length} aircraft (client[${activeClientIndex}])`);
  } catch (err) {
    consecutiveErrors++;
    console.warn('[OpenSkyDirect] Poll error:', err);
    emitStatus();
  }

  schedulePoll();
}

function schedulePoll(): void {
  if (!isRunning) return;
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = setTimeout(() => { void poll(); }, pollIntervalMs);
}

// ─── Viewport ────────────────────────────────────────────────────

export function setViewport(viewport: Viewport | null): void {
  currentViewport = viewport;
  if (!viewport) {
    pollIntervalMs = MAX_POLL_INTERVAL_MS;
  } else {
    const area = (viewport.lamax - viewport.lamin) * (viewport.lomax - viewport.lomin);
    if (area < 1)        pollIntervalMs = MIN_POLL_INTERVAL_MS;
    else if (area < 10)  pollIntervalMs = 10_000;
    else if (area < 100) pollIntervalMs = 15_000;
    else                 pollIntervalMs = MAX_POLL_INTERVAL_MS;
  }
  // Immediately reschedule to apply new settings
  if (isRunning && pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = setTimeout(() => { void poll(); }, 500); // quick next poll
  }
}

// ─── Public API ───────────────────────────────────────────────────

export async function startOpenSkyDirect(
  onUpdate: AircraftUpdateCallback,
  onStatus: StatusUpdateCallback,
): Promise<void> {
  if (isRunning) return;
  isRunning = true;
  onAircraftUpdate = onUpdate;
  onStatusUpdate = onStatus;

  // Fetch credentials from backend (admin endpoint, protected)
  try {
    const result = await apiClient.get<{ credentials: CredentialConfig[] }>(
      '/admin/opensky-credentials',
    );
    credentials = result.credentials ?? [];
    tokens = new Array(credentials.length).fill(null);
    activeClientIndex = 0;
    console.log(`[OpenSkyDirect] Loaded ${credentials.length} credentials`);
  } catch {
    console.warn('[OpenSkyDirect] No credentials available, using anonymous polling');
    credentials = [];
    tokens = [];
  }

  emitStatus();
  void poll(); // viewport guard inside poll() handles missing viewport
}

export function stopOpenSkyDirect(): void {
  isRunning = false;
  corsBlocked = false; // reset for potential restart
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  currentEntities = [];
  currentViewport = null;
  onAircraftUpdate = null;
  onStatusUpdate = null;
  console.log('[OpenSkyDirect] Stopped');
}

export function getOpenSkyDirectStatus(): OpenSkyDirectStatus {
  return buildStatus();
}
